import { useCallback, useEffect, useRef } from 'react';

export interface UseTimeoutOptions {
  /**
   * Whether to clear the previous timeout when another is scheduled
   *
   * @defaultValue true
   */
  cancel?: boolean;
}

/**
 * Schedule timeouts like `setTimeout` that get cleared when the component
 * unmounts.
 *
 * By default, scheduling a timeout will cancel any outstanding timeouts. Pass
 * `{ cancel: false }` to disable this behavior and always execute all timeout
 * callbacks unless the component is unmounted or the returned `clear` function
 * is called.
 *
 * @returns a tuple containing the timeout function and clear function
 */
export const useTimeout = ({ cancel = true }: UseTimeoutOptions = {}) => {
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clear = useCallback(() => {
    timeoutsRef.current.forEach(id => clearTimeout(id));
    timeoutsRef.current = [];
  }, []);

  const timeout = useCallback(
    (callback: () => void, duration: number) => {
      if (cancel) clear();

      const id = setTimeout(callback, duration);

      timeoutsRef.current.push(id);

      return id;
    },
    [cancel, clear],
  );

  useEffect(() => clear, [clear]);

  return [timeout, clear] as const;
};
